/**
 * AuthContext — contexto global de autenticação.
 *
 * Responsabilidades:
 *  - Observa o estado de autenticação do Firebase.
 *  - Mantém o perfil do usuário em tempo real via onSnapshot.
 *  - Dispara auto-unlock de badges quando totalBooksRead aumenta.
 *
 * Consumido via hook useAuth() (re-exportado de src/hooks/useAuth.tsx
 * para backward compatibility com todo o código existente).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { User } from "firebase/auth";
import {
  consumeOAuthRedirectResult,
  isOAuthRedirectPending,
  onUserAuthStateChanged,
} from "../services/firebase/authService";
import {
  ensureUserProfile,
  subscribeToUserProfile,
  type UserProfile,
} from "../services/firebase/userService";
import { bootMark, traceStep } from "../lib/boot/bootTrace";
import { BOOT_TIMEOUT_MS, isBootTimeout, withTimeout } from "../lib/boot/withTimeout";

/** Teto da leitura do claim `admin` no boot (BUG-260). Não-fatal: estourou = não-admin. */
const BOOT_CLAIMS_TIMEOUT_MS = 4_000;

// ---------------------------------------------------------------------------
// Contrato público
// ---------------------------------------------------------------------------

export interface AuthContextData {
  user:           User | null;
  profile:        UserProfile | null;
  isLoading:      boolean;
  /**
   * `true` quando `user` é uma sessão anônima do Firebase (F1.5 do Roadmap
   * PRO — visitante sem conta lendo um perfil público). Nunca é uma conta de
   * verdade: sem `profile`, sem `isPro`, sem badges. Todo guard de rota que
   * trata "logado" trata isto como "sem sessão" — só o guard do perfil
   * público (`AnonymousOrGuarded`) admite este estado de propósito.
   */
  isAnonymous:    boolean;
  /** true enquanto um login Google por redirect está sendo finalizado (BUG-30) */
  redirectPending: boolean;
  /** Força re-leitura pontual do perfil (compatibilidade com código legado) */
  refreshProfile: () => void;
  /** true se o perfil no Firestore tem isPro */
  effectiveIsPro: boolean;
  /**
   * true se o ID token carrega o custom claim `admin` (BUG-46). É a fonte do
   * guard de rota do /admin — cortesia, não fronteira de segurança: o gate real
   * é server-side em cada callable (`assertAdmin`). NÃO vem de `users.isAdmin`,
   * que o client conseguia forjar via create/recreate do próprio doc.
   */
  isAdmin: boolean;
  /**
   * Código da etapa de boot que estourou o teto de 10s (`BOOT_AUTH_TIMEOUT`,
   * `BOOT_PROFILE_TIMEOUT`, `BOOT_PROFILE_SYNC_TIMEOUT`) ou que FALHOU de
   * verdade (`BOOT_PROFILE_ERROR`, `BOOT_PROFILE_SYNC_ERROR` — BUG-202), ou
   * `null` quando o boot está saudável. Quem renderiza a tela de falha é o
   * `BootGate`.
   */
  bootFailure: string | null;
  /** Refaz as etapas de boot. É o "Tentar de novo" da tela de falha. */
  retryBoot: () => void;
}

// ---------------------------------------------------------------------------
// Contexto interno
// ---------------------------------------------------------------------------

export const AuthContext = createContext<AuthContextData | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,      setUser]      = useState<User | null>(null);
  const [profile,   setProfile]   = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // BUG-46: admin vem do custom claim `admin` no ID token, não de users.isAdmin.
  const [isAdmin,   setIsAdmin]   = useState(false);
  // BUG-30: true se voltamos de um redirect OAuth (Google ou Apple) e o
  // getRedirectResult ainda não finalizou. Mantém os botões de login travados
  // nessa janela (o flag de sessão sobrevive ao reload do redirect).
  const [redirectPending, setRedirectPending] = useState(() => isOAuthRedirectPending());

  /**
   * Código da etapa de boot que estourou (ex.: `BOOT_AUTH_TIMEOUT`), ou `null`
   * enquanto o boot corre normalmente. É o que a tela de falha mostra e o que a
   * pessoa consegue reportar por print — sem Mac não há Web Inspector, então
   * este código é o diagnóstico.
   */
  const [bootFailure, setBootFailure] = useState<string | null>(null);
  /** Incrementado pelo "Tentar de novo": re-executa os efeitos do boot. */
  const [bootAttempt, setBootAttempt] = useState(0);

  const profileUnsubRef = useRef<(() => void) | null>(null);

  const refreshProfile = useCallback(() => {
    // onSnapshot mantém o perfil atualizado; callback mantido para compat. legada.
  }, []);

  const retryBoot = useCallback(() => {
    // BUG-181: o "Tentar de novo" re-executava os efeitos de boot sobre a
    // MESMA instância do SDK — e quando quem travou é o próprio SDK (iframe de
    // auth pendurado na web, transporte do Firestore morto pós-suspensão),
    // retry nenhum cura. Relato de campo (29/08): *"clico para tentar
    // novamente e a página continua não funcionando; só abrindo outra guia com
    // a URL funciona"* — a guia nova funciona porque é um PROCESSO novo, e
    // recarregar entrega o mesmo processo novo sem a pessoa precisar descobrir
    // o truque. A 1ª tentativa segue em-place (barata, cobre o transitório e
    // preserva o painel do bootTrace para o print); da 2ª em diante, recarrega.
    // Reload por TOQUE não precisa de orçamento — quem tem teto é reload
    // automático (chunkReload, firestoreHealth), senão vira loop de boot.
    if (bootAttempt >= 1) {
      window.location.reload();
      return;
    }
    bootMark("retry", "info", `attempt=${bootAttempt + 1}`);
    setBootFailure(null);
    setIsLoading(true);
    setBootAttempt((n) => n + 1);
  }, [bootAttempt]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await traceStep("oauth-redirect", () =>
          withTimeout(consumeOAuthRedirectResult(), "BOOT_REDIRECT_TIMEOUT"),
        );
      } catch (e) {
        if (!cancelled) {
          console.warn("[Lura] OAuth redirect result:", (e as Error).message);
        }
      } finally {
        // Redirect finalizado (sucesso, cancelado ou erro) → libera o botão.
        // Esta etapa NÃO derruba o boot quando estoura: ela só governa o estado
        // do botão de login. Travar a abertura do app por causa dela seria
        // trocar um spinner infinito por uma tela de erro infinita.
        if (!cancelled) setRedirectPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootAttempt]);

  useEffect(() => {
    let isMounted = true;
    let authEventVersion = 0;
    let sawFirstAuthEvent = false;
    // Vive no escopo do efeito (e não dentro do callback) porque o cleanup
    // precisa alcançá-lo: um timer sobrevivente dispararia `setBootFailure`
    // depois do unmount, e em iOS ainda segura o event loop acordado.
    let profileTimer: ReturnType<typeof setTimeout> | undefined;

    /**
     * Teto do PRIMEIRO `onAuthStateChanged`.
     *
     * Este é o await mais perigoso do boot e o menos óbvio: ele não parece um
     * await. `isLoading` só sai de `true` dentro deste callback, e o SDK só
     * dispara o callback depois de `_initializationPromise` — que, quando o Auth
     * tem `popupRedirectResolver`, inclui carregar `apis.google.com` e embutir o
     * iframe do authDomain. Sem teto, rede ruim = spinner para sempre.
     */
    bootMark("auth-state", "start");
    const authTimer = setTimeout(() => {
      if (!isMounted || sawFirstAuthEvent) return;
      bootMark("auth-state", "timeout", "primeiro evento não chegou");
      setBootFailure("BOOT_AUTH_TIMEOUT");
    }, BOOT_TIMEOUT_MS);

    const authUnsub = onUserAuthStateChanged(async (firebaseUser) => {
      const currentVersion = ++authEventVersion;
      if (!sawFirstAuthEvent) {
        sawFirstAuthEvent = true;
        clearTimeout(authTimer);
        bootMark("auth-state", "ok", firebaseUser ? "com sessão" : "sem sessão");
      }

      if (profileUnsubRef.current) {
        profileUnsubRef.current();
        profileUnsubRef.current = null;
      }

      setUser(firebaseUser);

      if (!firebaseUser) {
        setProfile(null);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      // F1.5 do Roadmap PRO (11/09): sessão anônima existe só para satisfazer
      // `isAuthed()` nas Rules de quem visita um perfil público sem conta —
      // ela NUNCA ganha `users/{uid}` nem `user_stats/{uid}`. Rodar
      // `ensureUserProfile` aqui criaria essas duas coleções para cada visita
      // anônima, uma conta fantasma por visitante. `profile` fica `null` de
      // propósito (não há o que sincronizar), e `isLoading` assenta direto —
      // não há perfil algum para esperar.
      if (firebaseUser.isAnonymous) {
        setProfile(null);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      // Lê o claim `admin` do ID token (cacheado). Depois de conceder o claim
      // via scripts/set-admin-claim.cjs, o founder precisa re-logar pra o token
      // novo carregar — o guard de rota reflete isso. Falha na leitura = não-admin.
      //
      // BUG-260: com o token EXPIRADO (>1 h desde a última renovação — o caso
      // de quem abre o app de manhã) esta chamada vai à rede, e o teto interno
      // do SDK do Auth é de 30–60 s. Era o único await do boot sem teto
      // próprio: rede ruim = tela de boot muda por até um minuto. O teto aqui
      // é curto e NÃO derruba o boot — estourou, segue como não-admin (o
      // Firestore renova o token por conta própria quando a rede responder).
      //
      // 17/09 (rodada de performance): NÃO é mais aguardado. O claim só governa
      // o guard do `/admin`, e esperá-lo aqui punha a ida à rede (token com
      // mais de 1 h = renovação no securetoken) EM SÉRIE antes do perfil — o
      // boot de quem abre o app de manhã pagava um round-trip a mais só para
      // saber se é admin. Corre em paralelo com o perfil; o `AdminShell` lê
      // `isAdmin` quando ele assentar (e ele assenta antes de qualquer chunk
      // de admin terminar de baixar).
      void withTimeout(
        firebaseUser.getIdTokenResult(),
        "BOOT_CLAIMS_TIMEOUT",
        BOOT_CLAIMS_TIMEOUT_MS,
      )
        .then((tokenResult) => {
          if (isMounted && currentVersion === authEventVersion) {
            setIsAdmin(tokenResult.claims.admin === true);
          }
        })
        .catch(() => {
          if (isMounted && currentVersion === authEventVersion) setIsAdmin(false);
        });

      // `getDoc` é offline-first: sem conexão ele NÃO rejeita, ele espera. Certo
      // dentro do app, errado no caminho da primeira tela — daí o teto.
      try {
        await traceStep("ensure-profile", () =>
          withTimeout(
            ensureUserProfile({
              uid:         firebaseUser.uid,
              email:       firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL:    firebaseUser.photoURL,
            }),
            "BOOT_PROFILE_TIMEOUT",
          ),
        );
      } catch (err) {
        if (!isMounted || currentVersion !== authEventVersion) return;
        if (isBootTimeout(err)) {
          setBootFailure(err.code);
          return;
        }
        // BUG-202: erro REAL também é falha de boot — e nunca mais "seguir com
        // `profile` nulo". Esse era o comportamento histórico da web, e o que
        // ele produzia na prática: `profile === null` com `isLoading === false`
        // é exatamente o estado que o `OnboardingGuard` lia como "conta nova"
        // e mandava um usuário CONCLUÍDO refazer o wizard — cujo fecho as
        // rules então recusam (`lifetimeBooksRead` só entra na transição
        // false → true). Medido em produção (03/09): o `getDoc` do perfil
        // rejeita quando a fila do SDK do Firestore já morreu por um
        // `INTERNAL ASSERTION FAILED` (b815) na mesma página; recarregar é o
        // que cura, e é o que o 2º "Tentar de novo" faz. `isLoading` fica em
        // `true` de propósito: é o que mantém as rotas na tela de boot em vez
        // de decidir com dado que não existe.
        console.error("[Lura] ensureUserProfile error:", (err as Error).message);
        setBootFailure("BOOT_PROFILE_ERROR");
        return;
      }

      if (!isMounted || currentVersion !== authEventVersion) return;

      // Teto do PRIMEIRO snapshot do perfil. O `onSnapshot` é a outra porta sem
      // teto: quando o Firestore não consegue abrir o canal, ele não chama nem
      // `next` nem `error` — fica tentando reconectar, que é o desenho dele.
      // `isLoading` depende deste callback, então sem isto o spinner é eterno.
      let sawFirstSnapshot = false;
      bootMark("profile-snapshot", "start");
      profileTimer = setTimeout(() => {
        if (!isMounted || sawFirstSnapshot || currentVersion !== authEventVersion) return;
        bootMark("profile-snapshot", "timeout", "nem next nem error");
        setBootFailure("BOOT_PROFILE_SYNC_TIMEOUT");
      }, BOOT_TIMEOUT_MS);

      const settleFirstSnapshot = (phase: "ok" | "fail", detail?: string) => {
        if (sawFirstSnapshot) return;
        sawFirstSnapshot = true;
        clearTimeout(profileTimer);
        bootMark("profile-snapshot", phase, detail);
      };

      const profileUnsub = subscribeToUserProfile(
        firebaseUser.uid,
        (updatedProfile) => {
          if (!isMounted || currentVersion !== authEventVersion) return;
          settleFirstSnapshot("ok", updatedProfile ? "perfil carregado" : "doc ausente");
          setProfile(updatedProfile);
          setIsLoading(false);
        },
        (err) => {
          if (!isMounted || currentVersion !== authEventVersion) return;
          const neverDelivered = !sawFirstSnapshot;
          settleFirstSnapshot("fail", err.message);
          console.error("[Lura] subscribeToUserProfile error:", err.message);
          // BUG-202: o listener morreu ANTES de entregar o primeiro perfil.
          // Baixar `isLoading` aqui deixaria `profile === null` decidindo
          // rota (→ onboarding). Depois de um perfil entregue, o último valor
          // bom fica na tela (keep-last-good, como o `UserStatsContext`).
          if (neverDelivered) {
            setBootFailure("BOOT_PROFILE_SYNC_ERROR");
            return;
          }
          setIsLoading(false);
        }
      );

      if (!isMounted || currentVersion !== authEventVersion) {
        clearTimeout(profileTimer);
        profileUnsub();
        return;
      }

      profileUnsubRef.current = profileUnsub;
    });

    return () => {
      isMounted = false;
      authEventVersion += 1;
      clearTimeout(authTimer);
      clearTimeout(profileTimer);
      authUnsub();
      if (profileUnsubRef.current) profileUnsubRef.current();
    };
  }, [bootAttempt]);

  const effectiveIsPro = profile?.isPro === true;
  const isAnonymous = user?.isAnonymous === true;

  const value = useMemo(
    () => ({
      user,
      profile,
      isLoading,
      isAnonymous,
      redirectPending,
      refreshProfile,
      effectiveIsPro,
      isAdmin,
      bootFailure,
      retryBoot,
    }),
    [
      user,
      profile,
      isLoading,
      isAnonymous,
      redirectPending,
      refreshProfile,
      effectiveIsPro,
      isAdmin,
      bootFailure,
      retryBoot,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook público
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextData {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return ctx;
}
